import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiX, FiCheck, FiRefreshCw, FiAlertCircle, FiUserCheck, FiMinusCircle } = FiIcons;

const LeaveCycleModal = ({ isOpen, onClose, teamMembers, onConfirm }) => {
  const [credit, setCredit] = useState({ casual: 1.0, paid: 1.0 });
  const [selectedIds, setSelectedIds] = useState([]);

  // Select all by default when opening
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(teamMembers.map(m => m.id));
    }
  }, [isOpen, teamMembers]);

  const handleToggleMember = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(memberId => memberId !== id)
        : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    if (selectedIds.length === teamMembers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(teamMembers.map(m => m.id));
    }
  };

  const handleSubmit = () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one employee.');
      return;
    }
    onConfirm(selectedIds, Number(credit.casual), Number(credit.paid));
    onClose();
  };

  if (!isOpen) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  };

  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        variants={modalVariants}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SafeIcon icon={FiRefreshCw} className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Run Monthly Cycle</h2>
              <p className="text-sm text-gray-500">Credit casual and paid leaves to employees</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Step 1: Set Amounts */}
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
              <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center text-xs mr-2">1</span>
              Set Credit Amounts
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Casual Leaves to Add</label>
                <input 
                  type="number" 
                  step="0.5"
                  value={credit.casual}
                  onChange={(e) => setCredit({ ...credit, casual: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Paid Leaves to Add</label>
                <input 
                  type="number" 
                  step="0.5"
                  value={credit.paid}
                  onChange={(e) => setCredit({ ...credit, paid: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Step 2: Select Employees */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <span className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs mr-2">2</span>
                Select Employees & Preview ({selectedIds.length} selected)
              </h3>
              <button 
                onClick={handleToggleAll}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                {selectedIds.length === teamMembers.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.length === teamMembers.length && teamMembers.length > 0}
                        onChange={handleToggleAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3 text-center">Current Casual</th>
                    <th className="px-4 py-3 text-center bg-blue-50 text-blue-700">New Casual</th>
                    <th className="px-4 py-3 text-center">Current Paid</th>
                    <th className="px-4 py-3 text-center bg-blue-50 text-blue-700">New Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {teamMembers.map(member => {
                    const isSelected = selectedIds.includes(member.id);
                    const currentCasual = member.leaveBalance?.casual || 0;
                    const currentPaid = member.leaveBalance?.paid || 0;
                    
                    return (
                      <tr key={member.id} className={isSelected ? 'bg-white' : 'bg-gray-50 opacity-60'}>
                        <td className="px-4 py-3">
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => handleToggleMember(member.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{member.name}</td>
                        <td className="px-4 py-3 text-center text-gray-500">{currentCasual}</td>
                        <td className="px-4 py-3 text-center font-bold text-blue-600 bg-blue-50">
                          {isSelected ? (Number(currentCasual) + Number(credit.casual)) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-500">{currentPaid}</td>
                        <td className="px-4 py-3 text-center font-bold text-blue-600 bg-blue-50">
                          {isSelected ? (Number(currentPaid) + Number(credit.paid)) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Confirm & Update Balances</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LeaveCycleModal;